import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Soft pastel palette — card bg + matching avatar bg (ultra light)
const pastelPalette = [
  {
    card: "from-[#fffef9] to-[#fffcf4]",
    avatar: "linear-gradient(135deg, #fef9ec 0%, #fffef9 100%)",
  }, // sand
  {
    card: "from-[#fafefe] to-[#f2fcf9]",
    avatar: "linear-gradient(135deg, #e8f9f4 0%, #fafefe 100%)",
  }, // mint
  {
    card: "from-[#fdfcff] to-[#f8f6ff]",
    avatar: "linear-gradient(135deg, #f0ecff 0%, #fdfcff 100%)",
  }, // lavender
  {
    card: "from-[#fffdf9] to-[#fff6f2]",
    avatar: "linear-gradient(135deg, #feeee8 0%, #fffdf9 100%)",
  }, // peach
  {
    card: "from-[#fefff8] to-[#fbfef0]",
    avatar: "linear-gradient(135deg, #f6fbde 0%, #fefff8 100%)",
  }, // butter yellow
  {
    card: "from-[#f8fdff] to-[#eef7ff]",
    avatar: "linear-gradient(135deg, #e0f1ff 0%, #f8fdff 100%)",
  }, // sky blue
  {
    card: "from-[#fffafd] to-[#fef2f9]",
    avatar: "linear-gradient(135deg, #fce2f2 0%, #fffafd 100%)",
  }, // blush pink
  {
    card: "from-[#f7fefa] to-[#ecfbf1]",
    avatar: "linear-gradient(135deg, #dcf5e6 0%, #f7fefa 100%)",
  }, // soft green
  {
    card: "from-[#fffefc] to-[#fef8ee]",
    avatar: "linear-gradient(135deg, #fdefd4 0%, #fffefc 100%)",
  }, // apricot
  {
    card: "from-[#f6fcff] to-[#eaf5ff]",
    avatar: "linear-gradient(135deg, #d4eeff 0%, #f6fcff 100%)",
  }, // ice blue
];

const instructors = [
  {
    name: "Jewel - Lead Mentor",
    germanLanguageSkill: "C1 - 2015",
    education: "Automation Engineering",
    institution: "Technical University of Applied Sciences Wildau",
    img: "/assets/img/instructor/jewel-1.png",
  },
  {
    name: "Zafor - Lead Instructor",
    germanLanguageSkill: "C1 - 2015",
    education: "Mechanical Engineering",
    institution: "Technical University of Applied Sciences Wildau",
    img: "/assets/img/instructor/zafor-1.png",
  },
  {
    name: "Akash - Senior Mentor",
    germanLanguageSkill: "B2 - 2015",
    education: "Information Engineering",
    institution: "Hamburg University of Applied Sciences",
    img: "/assets/img/instructor/akash-1.png",
  },
  {
    name: "Nadim - Instructor",
    germanLanguageSkill: "B2 - 2025",
    education: "Pre-University Education",
    institution: "College, Dhaka, Bangladesh",
    img: "/assets/img/instructor/nadim-1.png",
  },
  {
    name: "Nazifa - Instructor",
    germanLanguageSkill: "B2 - 2025",
    education: "Pre-University Education",
    institution: "College, Dhaka, Bangladesh",
    img: "/assets/img/instructor/nazifa-1.png",
  },
  {
    name: "Shuvo - Instructor",
    germanLanguageSkill: "B2 - Running",
    education: "Pre-University Education",
    institution: "College, Dhaka, Bangladesh",
    img: "/assets/img/instructor/shuvo-1.png",
  },
  {
    name: "Alif - Operations",
    germanLanguageSkill: "A2 - Running",
    education: "MSC in CSE - Running",
    institution: "University, Dhaka, Bangladesh",
    img: "/assets/img/instructor/alif-1.png",
  },
  {
    name: "Shazzad - Operations",
    germanLanguageSkill: "B2 - Running",
    education: "Pre-University Education",
    institution: "College, Dhaka, Bangladesh",
    img: "/assets/img/instructor/shazzad-1.png",
  },
  {
    name: "Tanvir - Operations",
    germanLanguageSkill: "B1 - Running",
    education: "Pre-University Education",
    institution: "College, Dhaka, Bangladesh",
    img: "/assets/img/instructor/tanvir-1.png",
  },
];

const AboutUsG2G = () => {
  const { t } = useTranslation();
  const ab = (key: string) => t(`about.${key}`);
  const missionItems: string[] = t("about.mission.items", { returnObjects: true }) as string[];

  return (
    <>
      <Breadcrumb title={ab("breadcrumb")} />

      <section className="bg-gray-50 py-16 font-sans">
        <div className="max-w-7xl mx-auto px-4">
          {/* Top Section */}
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#009fe3] mb-4 tracking-tight">
              {ab("welcomeTitle")}
            </h2>
            <div className="mx-auto w-16 h-1 bg-[#009fe3] rounded-full mb-5"></div>
            <p className="text-gray-600 max-w-3xl mx-auto text-base md:text-lg leading-relaxed">
              {ab("welcomeBody")}
            </p>
          </div>

          {/* Card: Image and About Details */}
          <div className="bg-white shadow-md hover:shadow-xl transition-all duration-300 rounded-2xl p-8 grid md:grid-cols-2 gap-10 items-center mb-16">
            <div className="overflow-hidden rounded-xl">
              <ImageWithBasePath
                src="assets/img/pages/about-us-g2g.png"
                alt="About Us"
                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
              />
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4 text-gray-800">
                {ab("mission.heading")}
              </h3>
              <p className="text-gray-700 mb-6 text-base md:text-lg">
                {ab("mission.body")}
              </p>

              <ul className="list-disc pl-5 text-gray-700 space-y-2 text-base">
                {missionItems.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="text-center mb-16">
            <h3 className="text-2xl font-semibold text-gray-800 mb-8">
              {ab("whyUs.heading")}
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                { emoji: "🎯", titleKey: "whyUs.card1Title", bodyKey: "whyUs.card1Body" },
                { emoji: "🇧🇩", titleKey: "whyUs.card2Title", bodyKey: "whyUs.card2Body" },
                { emoji: "⭐", titleKey: "whyUs.card3Title", bodyKey: "whyUs.card3Body" },
              ].map(({ emoji, titleKey, bodyKey }) => (
                <div key={titleKey} className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border border-[#009fe3]/10 hover:border-[#009fe3]/25 group relative overflow-hidden">
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#009fe3] to-[#007cb5] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>
                  <div className="w-16 h-16 bg-gradient-to-r from-[#009fe3]/10 to-[#007cb5]/10 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:from-[#009fe3] group-hover:to-[#007cb5] transition-all duration-300">
                    <span className="text-3xl group-hover:grayscale group-hover:brightness-0 group-hover:invert transition-all duration-300">{emoji}</span>
                  </div>
                  <h4 className="text-xl font-bold text-[#009fe3] mb-4 group-hover:text-[#007cb5] transition-colors duration-300">
                    {ab(titleKey)}
                  </h4>
                  <p className="text-gray-600 text-base leading-relaxed">
                    {ab(bodyKey)}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Featured Instructors ─── */}
          <div className="mb-16">
            {/* Section Header */}
            <div className="text-center mb-10">
              <h3 className="text-2xl font-semibold text-[#009fe3] mb-2">
                {ab("team.title")}
              </h3>
              <div className="mx-auto w-16 h-1 bg-[#009fe3] rounded-full mb-4"></div>
              <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
                {ab("team.subtitle")}
              </p>
            </div>

            {/* Instructor Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {instructors.map((instructor, index) => {
                const pastel = pastelPalette[index % pastelPalette.length];
                return (
                  <div
                    key={index}
                    className={`bg-gradient-to-br ${pastel.card} rounded-2xl shadow-md hover:shadow-xl transition-all duration-300 border border-white hover:border-[#009fe3]/30 group overflow-hidden`}
                  >
                    <div className="h-1 w-full bg-gradient-to-r from-[#009fe3] to-[#007cb5] transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300"></div>

                    <div className="p-6 flex flex-col items-center text-center">
                      <div className="relative mb-4">
                        <div
                          className="w-36 h-36 rounded-full overflow-hidden border-4 border-white group-hover:border-[#009fe3]/40 transition-all duration-300 shadow-md"
                          style={{ background: pastel.avatar }}
                        >
                          <img
                            src={instructor.img}
                            alt={instructor.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <span className="absolute -bottom-1 -right-1 text-base bg-white rounded-full shadow p-0.5">
                          🇩🇪
                        </span>
                      </div>

                      <h4 className="text-base font-bold text-gray-800 mb-1 group-hover:text-[#009fe3] transition-colors duration-300">
                        {instructor.name}
                      </h4>

                      {instructor.germanLanguageSkill && (
                        <span className="inline-block bg-white/70 text-[#009fe3] text-xs font-semibold px-3 py-1 rounded-full mb-3">
                          🗣 {ab("team.germanLevel")} {instructor.germanLanguageSkill}
                        </span>
                      )}

                      <div className="w-10 h-px bg-gray-300 mb-3"></div>

                      <p className="text-sm font-semibold text-[#009fe3] mb-0.5">
                        {instructor.education}
                      </p>
                      <p className="text-xs text-gray-500">
                        {instructor.institution}
                      </p>
                    </div>
                  </div>
                );
              })}

              {/* & Many More card */}
              <div className="bg-gradient-to-br from-[#009fe3]/10 to-[#007cb5]/10 rounded-2xl border-2 border-dashed border-[#009fe3]/30 hover:border-[#009fe3]/60 transition-all duration-300 group flex flex-col items-center justify-center p-6 min-h-64 text-center cursor-default">
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-md mb-4 group-hover:shadow-lg transition-all duration-300">
                  <span className="text-2xl">🌟</span>
                </div>
                <h4 className="text-lg font-bold text-[#009fe3] mb-2">
                  {ab("team.moreTitle")}
                </h4>
                <p className="text-sm text-gray-500 leading-relaxed max-w-xs">
                  {ab("team.moreBody")}
                </p>
              </div>
            </div>
          </div>

          {/* Call to Action */}
          <div className="text-center">
            <h4 className="text-2xl font-bold mb-4 text-gray-800">
              {ab("cta.title")}
            </h4>
            <p className="text-gray-600 mb-6 text-base md:text-lg">
              {ab("cta.body")}
            </p>
            <Link
              to="/course/courses"
              className="inline-block bg-[#009fe3] hover:bg-[#007cb5] text-white py-3 px-7 rounded-lg font-semibold transition duration-300 shadow"
            >
              {ab("cta.btn")}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default AboutUsG2G;
